import { useState, useRef, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useLocalizedMethods } from "@/hooks/useLocalizedMethods";
import { useConversations } from "@/hooks/useConversations";
import { useChatMessages } from "@/hooks/useChatMessages";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ScrollArea } from "@/components/ui/scroll-area";
import { SidebarProvider, useSidebar } from "@/components/ui/sidebar";
import { AppSidebar } from "@/components/AppSidebar";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { supabase } from "@/integrations/supabase/client";
import { Send, Sparkles, User as UserIcon, PanelLeftClose, PanelLeft } from "lucide-react";
import { useIsMobile } from "@/hooks/use-mobile";
import { BottomNav, Tab } from "@/components/BottomNav";
import { MethodsView } from "@/components/MethodsView";
import { SubscriptionBanner } from "@/components/SubscriptionBanner";
import { useSubscription } from "@/contexts/SubscriptionContext";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { AppLayout } from "@/components/AppLayout";

type Message = {
  role: "user" | "assistant";
  content: string;
};

const JuniChat = () => {
  const { t, i18n } = useTranslation();
  const { localizeMethods } = useLocalizedMethods();
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useIsMobile();
  const { subscribed, isTrialActive, loading: subscriptionLoading } = useSubscription();
  const [activeTab, setActiveTab] = useState<Tab>("chat");
  const [activeView, setActiveView] = useState<'chat' | 'methods' | 'loop'>('chat');
  const [input, setInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [rawMethods, setRawMethods] = useState<any[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [profile, setProfile] = useState<any>(null);
  const [userId, setUserId] = useState<string | undefined>();
  const [currentConversationId, setCurrentConversationId] = useState<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Conversation and message hooks
  const { conversations, createConversation, updateConversationTitle, deleteConversation } = useConversations(userId);
  const { messages, saveMessage, setMessages } = useChatMessages(currentConversationId);

  // Localized methods based on current language
  const methods = localizeMethods(rawMethods);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Load both methods and profile in parallel on mount
  useEffect(() => {
    const loadData = async () => {
      const [methodsResult, userResult] = await Promise.all([
        supabase.from("training_methods").select("*").order("category"),
        supabase.auth.getUser()
      ]);
      
      if (methodsResult.data) setRawMethods(methodsResult.data);
      
      if (userResult.data?.user) {
        setUserId(userResult.data.user.id);
        const { data: profileData } = await supabase
          .from("profiles")
          .select("*")
          .eq("id", userResult.data.user.id)
          .maybeSingle();
        if (profileData) setProfile(profileData);
      }
    };
    
    loadData();
  }, []);

  // Handle method selection from other pages via navigation state
  useEffect(() => {
    const state = location.state as { selectedMethod?: any; activeTab?: Tab };
    if (state?.selectedMethod) {
      handleMethodClick(state.selectedMethod);
      // Clear the state to prevent re-triggering
      navigate(location.pathname, { replace: true, state: {} });
    }
    if (state?.activeTab) {
      setActiveTab(state.activeTab);
      // Clear the state to prevent re-triggering
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state]);

  const streamChat = async (userMessage: string, conversationId: string) => {
    const CHAT_URL = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/chat-with-juni`;
    
    // Get current session for authentication
    const { data: { session } } = await supabase.auth.getSession();
    
    if (!session) {
      throw new Error(t('chat.error'));
    }

    // Build message history from DB
    const messageHistory = messages.map(m => ({
      role: m.role,
      content: m.content
    }));
    
    const resp = await fetch(CHAT_URL, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${session.access_token}`,
      },
      body: JSON.stringify({ 
        messages: [...messageHistory, { role: "user", content: userMessage }],
        language: i18n.language
      }),
    });

    if (!resp.ok || !resp.body) {
      if (resp.status === 429 || resp.status === 402) {
        const error = await resp.json();
        throw new Error(error.error);
      }
      throw new Error(t('chat.connectionError'));
    }

    const reader = resp.body.getReader();
    const decoder = new TextDecoder();
    let textBuffer = "";
    let streamDone = false;
    let assistantContent = "";

    // Create temporary assistant message for streaming
    const tempAssistantMsg = {
      id: 'temp-' + Date.now(),
      conversation_id: conversationId,
      role: 'assistant' as const,
      content: "",
      created_at: new Date().toISOString()
    };
    setMessages(prev => [...prev, tempAssistantMsg]);

    while (!streamDone) {
      const { done, value } = await reader.read();
      if (done) break;
      textBuffer += decoder.decode(value, { stream: true });

      let newlineIndex: number;
      while ((newlineIndex = textBuffer.indexOf("\n")) !== -1) {
        let line = textBuffer.slice(0, newlineIndex);
        textBuffer = textBuffer.slice(newlineIndex + 1);

        if (line.endsWith("\r")) line = line.slice(0, -1);
        if (line.startsWith(":") || line.trim() === "") continue;
        if (!line.startsWith("data: ")) continue;

        const jsonStr = line.slice(6).trim();
        if (jsonStr === "[DONE]") {
          streamDone = true;
          break;
        }

        try {
          const parsed = JSON.parse(jsonStr);
          const content = parsed.choices?.[0]?.delta?.content as string | undefined;
          if (content) {
            assistantContent += content;
            setMessages(prev => {
              const updated = [...prev];
              const lastMsg = updated[updated.length - 1];
              if (lastMsg && lastMsg.id === tempAssistantMsg.id) {
                updated[updated.length - 1] = { ...lastMsg, content: assistantContent };
              }
              return updated;
            });
          }
        } catch {
          textBuffer = line + "\n" + textBuffer;
          break;
        }
      }
    }

    // Save final assistant message to DB and replace temp message
    if (assistantContent) {
      // Remove temp message first
      setMessages(prev => prev.filter(m => m.id !== tempAssistantMsg.id));
      // Save to DB which will add the real message
      await saveMessage(conversationId, 'assistant', assistantContent);
    }
  };

  const handleSend = async () => {
    if (!input.trim() || isLoading || !userId) return;

    // Block if no subscription AND no trial
    if (!subscribed && !isTrialActive) {
      return;
    }

    const userMessage = input.trim();
    setInput("");
    
    let conversationId = currentConversationId;

    // Create new conversation if none exists
    if (!conversationId) {
      conversationId = await createConversation();
      if (!conversationId) return;
      setCurrentConversationId(conversationId);
      
      // Generate title after first message
      try {
        const { data } = await supabase.functions.invoke('generate-conversation-title', {
          body: { userMessage, language: i18n.language }
        });
        if (data?.title) {
          await updateConversationTitle(conversationId, data.title);
        }
      } catch (error) {
        console.error('Failed to generate title:', error);
      }
    }

    // Save user message to DB
    await saveMessage(conversationId, 'user', userMessage);
    setIsLoading(true);

    try {
      await streamChat(userMessage, conversationId);
    } catch (error) {
      console.error("Chat error:", error);
      const errorMessage = t('chat.errorMessage', { 
        error: error instanceof Error ? error.message : t('chat.connectionError') 
      });
      await saveMessage(conversationId, 'assistant', errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleMethodClick = (method: any) => {
    if (isLoading) return;
    
    // Get category explanation from translation
    const categoryExplanation = t(`categoryExplanations.${method.category}`, '');
    
    const introMessage = categoryExplanation 
      ? `${categoryExplanation}\n\n---\n\n${t('chat.methodIntro', {
          name: method.name,
          description: method.description,
          steps: method.steps.length,
          context: method.application_context
        })}`
      : t('chat.methodIntroSimple', {
          name: method.name,
          description: method.description,
          steps: method.steps.length,
          context: method.application_context
        });
    
    // Create a new conversation for the method
    const startMethodConversation = async () => {
      const newConvId = await createConversation(method.name);
      if (newConvId) {
        setCurrentConversationId(newConvId);
        await saveMessage(newConvId, 'assistant', introMessage);
      }
    };
    
    startMethodConversation();
    
    // Switch to chat tab on mobile after selecting method
    if (isMobile) {
      setActiveTab("chat");
    }
    
    scrollToBottom();
  };

  const handleTabChange = (tab: Tab) => {
    setActiveTab(tab);
    if (tab === "profile") {
      navigate("/profile");
    }
  };

  const categories = Array.from(new Set(methods.map(m => m.category)));
  const methodCounts = methods.reduce((acc, method) => {
    acc[method.category] = (acc[method.category] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);

  // Show loading screen while subscription status is being checked
  if (subscriptionLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-muted-foreground">{t('chat.loading')}</p>
        </div>
      </div>
    );
  }

  // Mobile: render different tabs
  if (isMobile) {
    return (
      <>
        <div className="min-h-screen flex flex-col">
          {/* Header */}
          <header className="h-16 border-b bg-card flex items-center px-4 gap-3 sticky top-0 z-40">
            <Sparkles className="h-6 w-6 text-primary flex-shrink-0" />
            <h1 className="text-lg font-bold truncate">Juni</h1>
            <div className="ml-auto">
              <Avatar className="w-8 h-8 cursor-pointer" onClick={() => navigate("/profile")}>
                <AvatarImage src={profile?.avatar_url} />
                <AvatarFallback>
                  {profile?.name?.charAt(0).toUpperCase() || <UserIcon className="w-4 h-4" />}
                </AvatarFallback>
              </Avatar>
            </div>
          </header>

          {/* Tab Content */}
          <div className="flex-1 overflow-hidden">
            {/* Chat Tab */}
            {activeTab === "chat" && (
              <Card className="h-full flex flex-col border-0 rounded-none">
                <CardHeader className="border-b">
                  <CardTitle className="text-base">{t('chat.title', 'Chat mit Juni')}</CardTitle>
                </CardHeader>

                 <CardContent className="flex-1 p-3 overflow-hidden">
                   <ScrollArea className="h-full pr-2">
                     <div className="space-y-4">
                       <SubscriptionBanner />
                        {messages.length === 0 && (
                          <div className="text-muted-foreground py-12">
                            <Sparkles className="h-12 w-12 mx-auto mb-4 text-primary" />
                            <div className="prose prose-sm max-w-2xl mx-auto px-4">
                              <p className="whitespace-pre-line">{t('chat.welcomeMessage')}</p>
                            </div>
                          </div>
                        )}
                      
                      {messages.map((message, index) => (
                        <div
                          key={index}
                          className={`flex ${
                            message.role === "user" ? "justify-end" : "justify-start"
                          }`}
                        >
                          <div
                            className={`max-w-[85%] rounded-lg p-3 ${
                              message.role === "user"
                                ? "bg-primary text-primary-foreground"
                                : "bg-muted border border-border"
                            }`}
                          >
                            <p className="text-sm whitespace-pre-wrap">{message.content}</p>
                          </div>
                        </div>
                      ))}
                      
                      {isLoading && (
                        <div className="flex justify-start">
                          <div className="bg-muted rounded-lg p-3 border border-border">
                            <div className="flex space-x-2">
                              <div className="w-2 h-2 bg-primary rounded-full animate-bounce" style={{ animationDelay: "0ms" }}></div>
                              <div className="w-2 h-2 bg-primary rounded-full animate-bounce" style={{ animationDelay: "150ms" }}></div>
                              <div className="w-2 h-2 bg-primary rounded-full animate-bounce" style={{ animationDelay: "300ms" }}></div>
                            </div>
                          </div>
                        </div>
                      )}
                      <div ref={messagesEndRef} />
                    </div>
                  </ScrollArea>
                </CardContent>

                <div className="border-t p-3">
                  <div className="flex gap-2">
                    <Input
                      value={input}
                      onChange={(e) => setInput(e.target.value)}
                      onKeyPress={handleKeyPress}
                      placeholder={t('chat.placeholder')}
                      disabled={isLoading}
                      className="flex-1 text-sm"
                    />
                    <Button onClick={handleSend} disabled={isLoading || !input.trim()} size="sm">
                      <Send className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </Card>
            )}

            {/* Methods Tab */}
            {activeTab === "methods" && (
              <div className="h-full p-4">
                <MethodsView
                  methods={methods}
                  selectedCategory={selectedCategory}
                  onCategorySelect={setSelectedCategory}
                  onMethodClick={handleMethodClick}
                  categories={categories}
                />
              </div>
            )}
          </div>
        </div>

        {/* Footer - Above Bottom Navigation */}
        <footer className="py-2 text-center text-xs text-muted-foreground border-t bg-card mb-16">
          {t('footer.text')}{" "}
          <a 
            href="https://junaco.de" 
            target="_blank" 
            rel="noopener noreferrer"
            className="text-primary hover:underline font-medium"
          >
            {t('footer.linkText')}
          </a>
        </footer>

        {/* Bottom Navigation */}
        <BottomNav activeTab={activeTab} onTabChange={handleTabChange} />
      </>
    );
  }

  // Desktop: original layout
  return (
    <AppLayout
      activeView={activeView}
      onViewChange={setActiveView}
      profile={profile}
      conversations={conversations}
      currentConversationId={currentConversationId}
      onSelectConversation={setCurrentConversationId}
      onNewConversation={async () => {
        const newId = await createConversation();
        if (newId) setCurrentConversationId(newId);
      }}
      onDeleteConversation={async (id) => {
        await deleteConversation(id);
        if (id === currentConversationId) {
          setCurrentConversationId(null);
        }
      }}
      onRenameConversation={updateConversationTitle}
      methods={methods}
      onMethodClick={handleMethodClick}
    >
      <div className="flex-1 flex flex-col overflow-hidden">

          <div className="flex-1 flex gap-4 p-4 overflow-hidden">
            {/* Chat View */}
            {activeView === 'chat' && (
              <>
                {/* Chat area */}
                <Card className="flex-1 flex flex-col h-full max-w-5xl mx-auto w-full shadow-lg overflow-hidden">
                  <CardHeader className="border-b py-4 px-6 flex-shrink-0">
                    <CardTitle className="text-xl">{t('chat.title', 'Chat mit Juni')}</CardTitle>
                  </CardHeader>

                   <CardContent className="flex-1 p-6 overflow-hidden">
                     <ScrollArea className="h-full pr-4">
                       <div className="space-y-6">
                         <SubscriptionBanner />
                           {messages.length === 0 && (
                            <div className="text-muted-foreground py-16">
                              <Sparkles className="h-16 w-16 mx-auto mb-6 text-primary" />
                              <div className="prose prose-sm max-w-2xl mx-auto px-6">
                                <p className="whitespace-pre-line">{t('chat.welcomeMessage')}</p>
                              </div>
                            </div>
                          )}
                        
                        {messages.map((message, index) => (
                          <div
                            key={index}
                            className={`flex ${
                              message.role === "user" ? "justify-end" : "justify-start"
                            }`}
                          >
                            <div
                              className={`max-w-[75%] rounded-xl px-5 py-4 shadow-sm ${
                                message.role === "user"
                                  ? "bg-primary text-primary-foreground"
                                  : "bg-muted border border-border"
                              }`}
                            >
                              <p className="text-base leading-relaxed whitespace-pre-wrap">{message.content}</p>
                            </div>
                          </div>
                        ))}
                        
                        {isLoading && (
                          <div className="flex justify-start">
                            <div className="bg-muted rounded-xl px-5 py-4 border border-border shadow-sm">
                              <div className="flex space-x-2">
                                <div className="w-2.5 h-2.5 bg-primary rounded-full animate-bounce" style={{ animationDelay: "0ms" }}></div>
                                <div className="w-2.5 h-2.5 bg-primary rounded-full animate-bounce" style={{ animationDelay: "150ms" }}></div>
                                <div className="w-2.5 h-2.5 bg-primary rounded-full animate-bounce" style={{ animationDelay: "300ms" }}></div>
                              </div>
                            </div>
                          </div>
                        )}
                        <div ref={messagesEndRef} />
                      </div>
                    </ScrollArea>
                  </CardContent>

                  <div className="border-t p-4 flex-shrink-0">
                    <div className="flex gap-3">
                      <Input
                        value={input}
                        onChange={(e) => setInput(e.target.value)}
                        onKeyPress={handleKeyPress}
                        placeholder={t('chat.placeholder')}
                        disabled={isLoading}
                        className="flex-1 text-base"
                      />
                      <Button onClick={handleSend} disabled={isLoading || !input.trim()} size="lg">
                        <Send className="h-5 w-5" />
                      </Button>
                    </div>
                  </div>
                </Card>
              </>
            )}

            {/* Methods View */}
            {activeView === 'methods' && (
              <div className="flex-1 h-full max-w-6xl mx-auto w-full">
                <Card className="h-full shadow-lg overflow-hidden">
                  <CardHeader className="border-b py-4 px-6 flex-shrink-0">
                    <CardTitle className="text-xl">{t('methods.title', 'Trainingsmethoden')}</CardTitle>
                  </CardHeader>
                  <CardContent className="p-6 h-[calc(100%-5rem)] overflow-hidden">
                    <MethodsView
                      methods={methods}
                      selectedCategory={selectedCategory}
                      onCategorySelect={setSelectedCategory}
                      onMethodClick={handleMethodClick}
                      categories={categories}
                    />
                  </CardContent>
                </Card>
              </div>
            )}
          </div>
        </div>
    </AppLayout>
  );
};


export default JuniChat;
